/**
 * Buy Section - Dedicated variant picker and price update handler
 * Handles variant selection and price updates for buy-section blocks
 */

// Prevent redeclaration if script is loaded multiple times
if (typeof window.BuySectionInitialized === 'undefined') {
  window.BuySectionInitialized = true;

/**
 * Multi-Variant Handler - Allows adding multiple variants to cart at once
 */
class BuySectionMultiVariantHandler {
  constructor(container) {
    this.container = container;
    this.blockId = container.dataset.blockId;
    this.selectionsContainer = container.querySelector('[data-variant-selections]');
    this.addButton = container.querySelector('[data-add-variant-btn]');
    this.submitButton = container.querySelector('[data-multi-variant-submit]');
    this.successMessage = container.querySelector('[data-success-message]');
    
    // Find the image container in the parent buy-section
    const buySection = container.closest('.buy-section');
    this.imageContainer = buySection ? buySection.querySelector('[data-product-image]') : null;
    
    this.addText = container.dataset.addText || '+ Weitere Variante hinzufuegen';
    this.addTextSubsequent = container.dataset.addTextSubsequent || '+ Noch eine Variante';
    this.showDifferentText = container.dataset.showDifferentText === 'true';
    
    this.variantCount = 1;
    
    // Load variant data
    this.loadVariantData();
    
    this.init();
  }
  
  loadVariantData() {
    const variantDataScript = this.container.querySelector(`script[data-buy-section-variants="${this.blockId}"]`);
    
    if (!variantDataScript) {
      console.warn('Multi-variant: No variant data found');
      this.variantData = { variants: [] };
      return;
    }
    
    try {
      this.variantData = JSON.parse(variantDataScript.textContent);
      console.log('Multi-variant: Loaded variant data:', this.variantData);
      console.log('Multi-variant: First variant image:', this.variantData.variants[0]?.image);
    } catch (e) {
      console.error('Multi-variant: Failed to parse variant data', e);
      this.variantData = { variants: [] };
    }
  }
  
  init() {
    if (!this.selectionsContainer || !this.addButton || !this.submitButton) {
      console.warn('Multi-variant: Missing required elements');
      return;
    }
    
    this.addButton.addEventListener('click', () => this.addVariantRow());
    this.submitButton.addEventListener('click', (e) => this.handleSubmit(e));
    
    // Listen for select changes to update variant image and availability (event delegation)
    this.selectionsContainer.addEventListener('change', (e) => {
      if (e.target.matches('[data-variant-select]')) {
        const variantId = e.target.value;
        this.updateVariantImage(variantId);
        this.updateAvailability(variantId);
        
        // Update button text if feature is enabled
        const variant = this.getVariantById(variantId);
        if (variant) {
          this.updateBuyButtonText(variant);
        }
      }
    });
    
    // Set initial button text on load
    if (this.submitButton && this.submitButton.dataset.sliderControlsSelection === 'true') {
      const firstSelect = this.selectionsContainer.querySelector('[data-variant-select]');
      if (firstSelect) {
        const variant = this.getVariantById(firstSelect.value);
        if (variant) {
          this.updateBuyButtonText(variant);
        }
      }
    }
  }
  
  getVariantById(variantId) {
    const id = parseInt(variantId, 10);
    return this.variantData.variants.find(v => v.id === id);
  }
  
  updateVariantImage(variantId) {
    if (!this.imageContainer) {
      console.log('Multi-variant: No image container found');
      return;
    }
    
    const variant = this.getVariantById(variantId);
    console.log('Multi-variant: Selected variant', variant);
    
    if (!variant) {
      console.warn('Multi-variant: Variant not found for ID:', variantId);
      return;
    }
    
    if (!variant.image) {
      console.log('Multi-variant: No image for variant:', variant.title);
      return;
    }
    
    const img = this.imageContainer.querySelector('img');
    if (!img) {
      console.warn('Multi-variant: No img element found');
      return;
    }
    
    console.log('Multi-variant: Current image src:', img.src);
    console.log('Multi-variant: New image URL:', variant.image);
    
    // Don't update if it's the same image
    if (img.src === variant.image) {
      console.log('Multi-variant: Image is already showing this variant');
      return;
    }
    
    // Fade out, change src, fade in
    img.style.transition = 'opacity 0.2s ease';
    img.style.opacity = '0';
    
    setTimeout(() => {
      // Update src (force update by adding timestamp if needed)
      const imageUrl = variant.image;
      img.src = imageUrl;
      img.alt = variant.image_alt || variant.title;
      
      // Remove srcset if it exists (Shopify image_tag might add this)
      if (img.hasAttribute('srcset')) {
        img.removeAttribute('srcset');
      }
      
      // Ensure image loads before fading in
      img.onload = () => {
        img.style.opacity = '1';
        console.log('Multi-variant: Image loaded successfully');
      };
      
      // Also set onerror to debug
      img.onerror = () => {
        console.error('Multi-variant: Image failed to load:', variant.image);
        img.style.opacity = '1'; // Show it anyway
      };
      
      // Fallback in case onload doesn't fire
      setTimeout(() => {
        img.style.opacity = '1';
      }, 300);
    }, 200);
  }
  
  updateAvailability(variantId) {
    const buySection = this.container.closest('.buy-section');
    if (!buySection) return;
    
    const availabilityContainer = buySection.querySelector('[data-availability-container]');
    if (!availabilityContainer) return;
    
    const variant = this.getVariantById(variantId);
    if (!variant) return;
    
    const isAvailable = variant.available;
    
    // Update availability using alert classes
    const availabilityHTML = isAvailable 
      ? `
        <div class="alert alert--success inline-flex items-center gap-3">
          <svg class="icon icon-ellipse icon-xs flex-auto" viewBox="0 0 16 16" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle fill="currentColor" cx="8" cy="8" r="5"></circle>
            <circle cx="8" cy="8" r="6.5" stroke-opacity="0.3" stroke-width="3"></circle>
          </svg>
          <span class="text-sm font-medium leading-tight">Auf Lager, in 1-3 Werktagen geliefert</span>
        </div>
      `
      : `
        <div class="alert alert--error inline-flex items-center gap-3">
          <svg class="icon icon-ellipse icon-xs flex-auto" viewBox="0 0 16 16" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle fill="currentColor" cx="8" cy="8" r="5"></circle>
            <circle cx="8" cy="8" r="6.5" stroke-opacity="0.3" stroke-width="3"></circle>
          </svg>
          <span class="text-sm font-medium leading-tight">Ausverkauft</span>
        </div>
      `;
    
    availabilityContainer.innerHTML = availabilityHTML;
  }
  
  updateBuyButtonText(variant) {
    if (!this.submitButton) return;
    
    if (this.submitButton.dataset.sliderControlsSelection !== 'true') return;
    
    const buttonText = this.submitButton.querySelector('.btn-text');
    if (!buttonText) return;
    
    // Check if multiple variants are selected
    if (this.variantCount > 1) {
      // Use multi-variant template with amount
      const multiVariantTemplate = this.submitButton.dataset.multiVariantButtonTextTemplate || '';
      if (multiVariantTemplate) {
        buttonText.textContent = multiVariantTemplate.replace(/\[amount\]/g, this.variantCount.toString());
        return;
      }
    }
    
    // Use single variant template with variant name
    const template = this.submitButton.dataset.buttonTextTemplate || '';
    if (!template) return;
    
    const variantTitle = variant?.title || '';
    if (!variantTitle) return;
    
    if (variant.available === false) {
      return;
    }
    
    buttonText.textContent = template.replace(/\[variant\]/g, variantTitle);
  }
  
  addVariantRow() {
    this.variantCount++;
    
    const newRow = document.createElement('div');
    newRow.className = 'buy-section__variant-row';
    newRow.dataset.variantRow = '';
    
    // Build select options
    let optionsHtml = '';
    this.variantData.variants.forEach(variant => {
      const disabled = !variant.available ? 'disabled' : '';
      const soldOutText = !variant.available ? ` - ${this.getSoldOutText()}` : '';
      optionsHtml += `<option value="${variant.id}" data-available="${variant.available}" ${disabled}>${variant.title}${soldOutText}</option>`;
    });
    
    newRow.innerHTML = `
      <div class="buy-section__variant-select-wrapper">
        <select 
          class="buy-section__variant-select"
          data-variant-select
          aria-label="Variante ${this.variantCount}"
        >
          ${optionsHtml}
        </select>
        <button 
          type="button" 
          class="buy-section__remove-variant-btn"
          data-remove-variant-btn
          aria-label="Variante entfernen"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    `;
    
    // Add remove button listener
    const removeBtn = newRow.querySelector('[data-remove-variant-btn]');
    removeBtn.addEventListener('click', () => this.removeVariantRow(newRow));
    
    this.selectionsContainer.appendChild(newRow);
    
    // Update UI state
    this.updateUIState();
    
    // Update button text after adding variant
    const firstSelect = this.selectionsContainer.querySelector('[data-variant-select]');
    if (firstSelect) {
      const variant = this.getVariantById(firstSelect.value);
      if (variant) {
        this.updateBuyButtonText(variant);
      }
    }
  }
  
  removeVariantRow(row) {
    row.remove();
    this.variantCount--;
    this.updateUIState();
    
    // Update button text after removing variant
    const firstSelect = this.selectionsContainer.querySelector('[data-variant-select]');
    if (firstSelect) {
      const variant = this.getVariantById(firstSelect.value);
      if (variant) {
        this.updateBuyButtonText(variant);
      }
    }
  }
  
  updateUIState() {
    this.updateAddButtonText();
    this.updateSuccessMessage();
  }
  
  updateAddButtonText() {
    if (this.showDifferentText && this.variantCount > 1) {
      this.addButton.textContent = this.addTextSubsequent;
    } else {
      this.addButton.textContent = this.addText;
    }
  }
  
  updateSuccessMessage() {
    if (!this.successMessage) return;
    
    // Show success message when 2 or more variants are selected
    if (this.variantCount >= 2) {
      this.successMessage.removeAttribute('hidden');
      this.successMessage.classList.add('is-visible');
    } else {
      this.successMessage.setAttribute('hidden', '');
      this.successMessage.classList.remove('is-visible');
    }
  }
  
  getSoldOutText() {
    const buySection = this.container.closest('.buy-section');
    return buySection?.dataset.soldOutText || 'Ausverkauft';
  }
  
  async handleSubmit(event) {
    event.preventDefault();
    
    const selects = this.selectionsContainer.querySelectorAll('[data-variant-select]');
    const items = [];
    
    selects.forEach(select => {
      const variantId = parseInt(select.value, 10);
      if (variantId) {
        // Check if variant already exists in items, if so increment quantity
        const existingItem = items.find(item => item.id === variantId);
        if (existingItem) {
          existingItem.quantity++;
        } else {
          items.push({ id: variantId, quantity: 1 });
        }
      }
    });
    
    if (items.length === 0) {
      console.warn('Multi-variant: No items to add');
      return;
    }
    
    // Check for upsell toggles within this buy-section
    const buySection = this.container.closest('.buy-section');
    if (buySection) {
      const upsellToggles = buySection.querySelectorAll('[data-upsell-block] input[type="checkbox"][data-upsell-variant-id]:checked');
      upsellToggles.forEach(toggle => {
        const upsellVariantId = parseInt(toggle.dataset.upsellVariantId, 10);
        if (upsellVariantId) {
          console.log('Multi-variant: Adding upsell product:', upsellVariantId);
          // Check if upsell variant already exists in items
          const existingItem = items.find(item => item.id === upsellVariantId);
          if (existingItem) {
            existingItem.quantity++;
          } else {
            items.push({ id: upsellVariantId, quantity: 1 });
          }
        }
      });
    }
    
    // Disable button and show loading state
    this.submitButton.disabled = true;
    this.submitButton.classList.add('is-loading');
    
    try {
      const config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: items,
          sections: this.getSectionsToBundle(),
          sections_url: window.location.pathname
        })
      };
      
      const response = await fetch('/cart/add.js', config);
      const data = await response.json();
      
      if (!response.ok || data.status) {
        throw new Error(data.message || data.description || 'Error adding to cart');
      }
      
      // Trigger cart update event
      this.triggerCartUpdate(data);
      
      // Open cart drawer if available
      this.openCartDrawer();
      
    } catch (error) {
      console.error('Multi-variant: Error adding to cart', error);
      alert(error.message || 'Ein Fehler ist aufgetreten.');
    } finally {
      this.submitButton.disabled = false;
      this.submitButton.classList.remove('is-loading');
    }
  }
  
  getSectionsToBundle() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  }
  
  async triggerCartUpdate(addData) {
    try {
      if (window.theme?.utils?.fetchConfig && window.theme?.routes?.cart_url) {
        const cartResponse = await fetch(window.theme.routes.cart_url, window.theme.utils.fetchConfig());
        const cartJson = await cartResponse.json();
        cartJson.sections = addData.sections || {};
        
        if (window.theme?.pubsub?.publish && window.theme?.pubsub?.PUB_SUB_EVENTS) {
          window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        }
      }
      
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: addData } }));
    } catch (e) {
      console.warn('Multi-variant: Could not trigger cart update', e);
    }
  }
  
  openCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.show === 'function') {
      cartDrawer.show(this.submitButton);
    }
  }
}

class BuySectionVariantHandler {
  constructor(container) {
    this.container = container;
    this.blockId = container.dataset.blockId;
    this.productForm = document.getElementById(`ProductForm-${this.blockId}`);
    this.priceContainer = document.getElementById(`Price-${this.blockId}`);
    this.variantPicker = container.querySelector('variant-radios, variant-selects');
    
    if (!this.productForm || !this.priceContainer) {
      console.warn('Buy section: Missing required elements');
      return;
    }
    
    // Load variant data
    this.loadVariantData();
    
    this.init();
  }
  
  loadVariantData() {
    const variantDataScript = document.querySelector(`script[data-buy-section-variants="${this.blockId}"]`);
    
    if (!variantDataScript) {
      console.warn('Buy section: No variant data found');
      this.variantData = { variants: [] };
      return;
    }
    
    try {
      this.variantData = JSON.parse(variantDataScript.textContent);
    } catch (e) {
      console.error('Buy section: Failed to parse variant data', e);
      this.variantData = { variants: [] };
    }
  }
  
  getVariantById(variantId) {
    return this.variantData.variants.find(v => v.id === variantId);
  }
  
  init() {
    // Listen for variant changes on the form
    this.productForm.addEventListener('variant:change', this.handleVariantChange.bind(this));
    
    // Also listen on document in case event bubbles up
    // Check if variant picker belongs to this buy-section
    const variantPicker = this.container.querySelector('variant-radios, variant-selects');
    if (variantPicker) {
      // Listen for variant:change events that might bubble to document
      const handleDocumentVariantChange = (event) => {
        // Check if this event is from our variant picker by checking the form
        const eventForm = event.target?.closest('form');
        if (eventForm && eventForm.id === `ProductForm-${this.blockId}`) {
        this.handleVariantChange(event);
      }
      };
      document.addEventListener('variant:change', handleDocumentVariantChange);
      
      // Also listen directly to variant picker change events as fallback
      variantPicker.addEventListener('change', () => {
        // Small delay to ensure currentVariant is updated
        setTimeout(() => {
          if (variantPicker.currentVariant) {
            this.handleVariantChange({
              detail: {
                variant: variantPicker.currentVariant
              }
            });
          }
        }, 50);
      });
    }

    // Listen for productInfo:loaded event to re-apply custom button text after renderProductInfo completes
    const submitButton = this.productForm.querySelector('[type="submit"]');
    if (submitButton && submitButton.dataset.sliderControlsSelection === 'true') {
      document.addEventListener('productInfo:loaded', this.onProductInfoLoaded.bind(this));
    }
  }

  onProductInfoLoaded() {
    // Re-apply custom button text after product info has been loaded and button text may have been reset
    const submitButton = this.productForm.querySelector('[type="submit"]');
    if (!submitButton || submitButton.dataset.sliderControlsSelection !== 'true') return;

    const variantPicker = this.container.querySelector('variant-radios, variant-selects');
    if (!variantPicker || !variantPicker.currentVariant) {
      // Fallback: use stored variant title if available
      const lastVariantTitle = submitButton.dataset.lastVariantTitle;
      if (lastVariantTitle) {
        const template = submitButton.dataset.buttonTextTemplate || '';
        const buttonText = submitButton.querySelector('.btn-text');
        if (buttonText && template) {
          buttonText.textContent = template.replace(/\[variant\]/g, lastVariantTitle);
        }
      }
      return;
    }

    // Re-apply button text with current variant
    this.updateBuyButton({ 
      id: variantPicker.currentVariant.id,
      title: variantPicker.currentVariant.title,
      available: variantPicker.currentVariant.available
    });
  }
  
  handleVariantChange(event) {
    const variant = event.detail.variant;
    
    if (!variant) {
      console.warn('Buy section: No variant data in event');
      return;
    }
    
    // Update price display
    this.updatePrice(variant);
    
    // Update button state
    this.updateBuyButton(variant);
    
    // Update availability badge
    this.updateAvailability(variant);
  }
  
  updatePrice(variant) {
    if (!this.priceContainer) return;
    
    // Get pre-formatted price data
    const variantData = this.getVariantById(variant.id);
    
    if (!variantData) {
      console.warn('Buy section: Variant data not found for ID:', variant.id);
      return;
    }
    
    const onSale = variantData.compare_at_price && variantData.compare_at_price > variantData.price;
    const priceClasses = 'price text-2xl font-bold';
    const salePriceClasses = 'price price--on-sale text-2xl font-bold';
    
    let priceHTML = '';
    
    if (onSale) {
      priceHTML = `
        <div class="${salePriceClasses}">
          <span class="sr-only">${this.getSaleText()}</span>
          <span class="price__regular whitespace-nowrap">${variantData.price_formatted}</span>
          <span class="sr-only">${this.getRegularPriceText()}</span>
          <span class="price__sale inline-flex items-center h-auto relative">${variantData.compare_at_price_formatted}</span>
        </div>
      `;
    } else {
      priceHTML = `
        <div class="${priceClasses}">
          <span class="price__regular whitespace-nowrap">${variantData.price_formatted}</span>
        </div>
      `;
    }
    
    this.priceContainer.innerHTML = priceHTML;
  }
  
  updateBuyButton(variant) {
    const submitButton = this.productForm.querySelector('[type="submit"]');
    if (!submitButton) return;
    
    // Get variant data to check availability
    const variantData = this.getVariantById(variant.id);
    const isAvailable = variantData ? variantData.available : variant.available;
    
    submitButton.disabled = !isAvailable;
    
    const buttonText = submitButton.querySelector('.btn-text');
    if (buttonText) {
      if (!isAvailable) {
        buttonText.textContent = this.getSoldOutText();
      } else {
        // Check if variant selection controls button text is enabled
        if (submitButton.dataset.sliderControlsSelection === 'true') {
          const template = submitButton.dataset.buttonTextTemplate || '';
          if (template) {
            // Get variant title from variant object or variantData
            const variantTitle = variant.title || variantData?.title || '';
            if (variantTitle) {
              buttonText.textContent = template.replace(/\[variant\]/g, variantTitle);
            } else {
              buttonText.textContent = this.getAddToCartText();
            }
          } else {
            buttonText.textContent = this.getAddToCartText();
          }
      } else {
        buttonText.textContent = this.getAddToCartText();
      }
      }
    }
    
    // Store the custom text template on the button for later re-application
    if (submitButton.dataset.sliderControlsSelection === 'true' && isAvailable) {
      submitButton.dataset.lastVariantTitle = variant.title || variantData?.title || '';
    }
  }
  
  updateAvailability(variant) {
    const availabilityContainer = this.container.querySelector('[data-availability-container]');
    if (!availabilityContainer) return;
    
    // Get variant data to check availability
    const variantData = this.getVariantById(variant.id);
    const isAvailable = variantData ? variantData.available : variant.available;
    
    // Update availability using alert classes
    const availabilityHTML = isAvailable 
      ? `
        <div class="alert alert--success inline-flex items-center gap-3">
          <svg class="icon icon-ellipse icon-xs flex-auto" viewBox="0 0 16 16" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle fill="currentColor" cx="8" cy="8" r="5"></circle>
            <circle cx="8" cy="8" r="6.5" stroke-opacity="0.3" stroke-width="3"></circle>
          </svg>
          <span class="text-sm font-medium leading-tight">Auf Lager, in 1-3 Werktagen geliefert</span>
        </div>
      `
      : `
        <div class="alert alert--error inline-flex items-center gap-3">
          <svg class="icon icon-ellipse icon-xs flex-auto" viewBox="0 0 16 16" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle fill="currentColor" cx="8" cy="8" r="5"></circle>
            <circle cx="8" cy="8" r="6.5" stroke-opacity="0.3" stroke-width="3"></circle>
          </svg>
          <span class="text-sm font-medium leading-tight">Ausverkauft</span>
        </div>
      `;
    
    availabilityContainer.innerHTML = availabilityHTML;
  }
  
  // Translation helpers
  getSaleText() {
    return this.container.dataset.saleText || 'Sale price';
  }
  
  getRegularPriceText() {
    return this.container.dataset.regularPriceText || 'Regular price';
  }
  
  getSoldOutText() {
    return this.container.dataset.soldOutText || 'Sold out';
  }
  
  getAddToCartText() {
    return this.container.dataset.addToCartText || 'Add to cart';
  }
}

/**
 * Initialize upsell toggles within a buy-section
 * Sets data-product-form-id and handles variant visibility
 * Works for standard (form-based), multi-variant (dropdown), and Go Configurator modes
 * @param {HTMLElement} buySection - The buy-section container
 */
function initUpsellToggles(buySection) {
  const blockId = buySection.dataset.blockId;
  const productFormId = `ProductForm-${blockId}`;
  const productForm = document.getElementById(productFormId);
  
  // Check if this is multi-variant mode (no product form, has multi-variant container)
  const multiVariantContainer = buySection.querySelector('[data-multi-variant-container]');
  const isMultiVariantMode = !productForm && multiVariantContainer;
  
  // Check if this is Go Configurator mode
  const goConfiguratorContainer = buySection.querySelector('[data-kk-slimtag-configurator]');
  const isGoConfiguratorMode = !productForm && goConfiguratorContainer;
  
  // Determine mode for logging
  const mode = isGoConfiguratorMode ? 'go-configurator' : (isMultiVariantMode ? 'multi-variant' : 'standard');
  console.log('initUpsellToggles:', { blockId, mode, hasProductForm: !!productForm });
  
  // Find all upsell toggles within this buy-section
  const upsellCards = buySection.querySelectorAll('[data-upsell-block]');
  
  if (upsellCards.length === 0) {
    console.log('initUpsellToggles: No upsell cards found');
    return;
  }
  
  upsellCards.forEach(card => {
    // Skip if already initialized
    if (card.dataset.upsellInitialized) return;
    card.dataset.upsellInitialized = 'true';
    
    const toggle = card.querySelector('input[type="checkbox"]');
    if (!toggle) {
      console.warn('initUpsellToggles: No toggle checkbox found in card');
      return;
    }
    
    console.log('initUpsellToggles: Initializing toggle', {
      variantId: toggle.dataset.upsellVariantId,
      mode
    });
    
    if (isMultiVariantMode || isGoConfiguratorMode) {
      // Multi-variant/Go Configurator mode: mark toggle for respective handler
      toggle.setAttribute('data-configurator-mode', mode);
      // Always show upsell in these modes (multiple variants can be added)
      card.style.display = '';
    } else {
      // Standard mode: set the data-product-form-id so theme.js can find it when adding to cart
      toggle.setAttribute('data-product-form-id', productFormId);
      
      // Handle variant visibility (hide when main product variant is sold out)
      const updateVisibility = (available) => {
        card.style.display = available ? '' : 'none';
      };
      
      // Get variant data for availability checks
      const getVariantData = () => {
        const variantPicker = buySection.querySelector('variant-radios, variant-selects');
        const jsonEl = variantPicker?.querySelector('script[type="application/json"]');
        try {
          return jsonEl ? JSON.parse(jsonEl.textContent) : null;
        } catch (e) {
          return null;
        }
      };
      
      // Apply initial availability state
      const applyInitialAvailability = () => {
        const variantData = getVariantData();
        const idInput = productForm?.querySelector('input[name="id"]');
        
        if (variantData && idInput) {
          const currentId = idInput.value;
          const current = variantData.find(v => String(v.id) === String(currentId));
          if (current) {
            updateVisibility(!!current.available);
            return;
          }
        }
        // Default to visible if we can't determine availability
        updateVisibility(true);
      };
      
      // Listen for variant changes
      if (productForm) {
        productForm.addEventListener('variant:change', (e) => {
          const variant = e?.detail?.variant;
          if (!variant) return;
          updateVisibility(!!variant.available);
        });
        
        // Fallback: also respond to hidden id input change
        productForm.addEventListener('change', (e) => {
          if (e.target?.name === 'id') applyInitialAvailability();
        });
      }
      
      // Apply initial state
      applyInitialAvailability();
    }
  });
}

// Initialize all buy sections when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initBuySections);
} else {
  initBuySections();
}

function initBuySections() {
  // Initialize standard variant handlers
  const buySections = document.querySelectorAll('.buy-section[data-block-id]');
  buySections.forEach(section => {
    // Skip if already initialized
    if (section.dataset.buySectionInitialized) return;
    section.dataset.buySectionInitialized = 'true';
    new BuySectionVariantHandler(section);
    
    // Initialize upsell toggles within this buy-section
    initUpsellToggles(section);
  });
  
  // Initialize multi-variant handlers
  const multiVariantContainers = document.querySelectorAll('[data-multi-variant-container]');
  multiVariantContainers.forEach(container => {
    // Skip if already initialized
    if (container.dataset.multiVariantInitialized) return;
    container.dataset.multiVariantInitialized = 'true';
    new BuySectionMultiVariantHandler(container);
  });
}

// Re-initialize when sections are dynamically added (for theme editor)
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    initBuySections();
  });
  
  document.addEventListener('shopify:block:select', () => {
    initBuySections();
  });
}

// Listen for buy-section:blocks-moved event to initialize upsell toggles
// This handles edge cases where blocks are moved after initial init
document.addEventListener('buy-section:blocks-moved', (e) => {
  const buySection = e.target.closest('.buy-section[data-block-id]');
  if (buySection) {
    initUpsellToggles(buySection);
  }
});

// Export initUpsellToggles for external use if needed
window.initUpsellToggles = initUpsellToggles;

} // End of BuySectionInitialized check
